import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { PayloadAction } from '@reduxjs/toolkit';
import { PLG_PENDO_EVENTS } from 'constants/PlgPendoEvents.constants';
import { pick } from 'lodash-es';
import Long from 'long';
import { useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { usePathname } from '@polyai/common/hooks/useRouter';
import { MAIN_BRANCH_ID } from '@polyai/stream/constants';
import type { ConflictResolution } from '@polyai/stream/protobuf/commands';
import {
  EventBatch,
  type Event as EventPB,
  type Notification as NotificationPB,
} from '@polyai/stream/protobuf/commands';
import type { RootState as ProjectionState } from '@polyai/stream/store';
import { Loader } from '@polyai/ui/components/atoms/Loader';

import type { RootState } from 'reduxStore';
import { HTTPError } from 'api/error';
import { AssistantType } from 'api/types';
import CreateBranchModal from 'components/organisms/CreateBranchModal';
import useProjection from 'hooks/agentStream/projection/useProjection';
import { useNotificationSocket } from 'hooks/agentStream/useNotificationSocket';
import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import useProject from 'hooks/useProject';
import { useProjectSettings } from 'hooks/useProjectSettings';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';
import useTemplateEngineUser from 'hooks/useTemplateEngineUser/useTemplateEngineUser';
import { useToast } from 'hooks/useToast';
import { isGlotCreatedProject, trackPendoEvent } from 'lib/pendo';
import ErrorPage, { ErrorStatus } from 'screens/ErrorPage';
import { rollBackProjection } from 'store/reducers/hydration';
import {
  addOptimisticCommands,
  clearActionQueue,
  clearOptimisticCommands,
  setBranchId,
  setLastKnownSequence,
} from 'store/reducers/stream-tracker';
import { agentStreamLogger } from '../constants';
import { useCommandSender, useDraftManager, useEventProcessor } from '../hooks';
import useAutoCreateBranch from '../hooks/useAutoCreateBranch';
import type {
  ActionWithCommandId,
  CommandPromiseResolver,
  DispatchEventContextType,
} from '../types';
import { AGENT_STREAM_CONTEXT } from './AgentStreamContext.uiCopy';
import { PreviewVersionProvider } from './PreviewVersionContext';

export const AgentStreamContext = createContext<DispatchEventContextType>(
  new Proxy({} as DispatchEventContextType, {
    get: () => {
      throw new Error('AgentStreamContext is not initialized');
    },
  }),
);

interface AgentStreamProviderProps {
  accountId: string;
  projectId: string;
  branchId: string;
  children: React.ReactNode;
  hideLoader?: boolean;
}

export const AgentStreamProviderInternal = ({
  accountId,
  projectId,
  branchId,
  children,
  hideLoader,
}: AgentStreamProviderProps) => {
  const pathname = usePathname();
  const store = useStore<RootState>();
  const dispatch = useLocalDispatch();
  const toast = useToast();
  const isPreviewMode = useAppSelector(
    state => state.project.preview.deploymentId,
  );
  const disconnectedTimeout = useRef<NodeJS.Timeout | null>(null);

  const isAgentWizard = pathname.includes('/agent-wizard');
  const { isMultiDraftEnabled } = useProjectSettings();
  const isMultiDraft = !isAgentWizard && isMultiDraftEnabled;
  const { isTemplateEngineUser } = useTemplateEngineUser();
  const { isFeatureOn } = useIsFeatureOn();
  const isDisconnectToastEnabled = isFeatureOn('realtime_disconnect_toast');
  // disabled on component unmount or during branch switches
  const shouldShowDisconnectToast = useRef(true);
  const previousBranchId = useRef<string>(branchId);

  const acceptedState = useRef<ProjectionState>(store.getState());

  const optimisticCommands = useAppSelector(
    state => state.streamTracker.optimisticCommands,
  );
  const actionQueue = useMemo(
    () => optimisticCommands.filter(a => a.queued),
    [optimisticCommands],
  );
  const queuedResolvers = useRef<CommandPromiseResolver[]>([]);
  const lastKnownSequence = useAppSelector(
    state => state.streamTracker.lastKnownSequence,
  );

  const { mutate: sendCommandsToServer, isLoading: isSendingCommands } =
    useCommandSender({ accountId, projectId, branchId });

  const { deleteDraft, isDeletingDraft, mergeDraft, isMergingDraft } =
    useDraftManager();

  const {
    data: projectionData,
    isLoading: isLoadingProjection,
    error: projectionError,
    isFirstLoad,
    status: projectionRequestStatus,
    dataUpdatedAt: projectionDataUpdatedAt,
    refetch: refetchProjection,
    isSampleProject,
  } = useProjection(accountId, projectId, branchId);

  const {
    handleServerEvents,
    handleServerNotification,
    handleServerProjection,
  } = useEventProcessor({
    accountId,
    projectId,
    branchId,
    store,
    acceptedState,
    refetchProjection,
  });

  // Track when projection has been hydrated into Redux
  const [isProjectionHydrated, setIsProjectionHydrated] = useState(false);

  // Reset hydration state when project changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  useEffect(() => {
    setIsProjectionHydrated(false);
  }, [accountId, projectId]);

  // Update the projection state when projection data is updated
  // This is to ensure the projection is hydrated when loading from the cache
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing
  useEffect(() => {
    if (projectionData && projectionRequestStatus === 'success') {
      handleServerProjection(projectionData);
      setIsProjectionHydrated(true);
    }
  }, [
    projectionData,
    handleServerProjection,
    accountId,
    projectId,
    branchId,
    projectionRequestStatus,
    projectionDataUpdatedAt,
  ]);

  // Disable socket for sample projects (they're read-only)
  const isSocketEnabled =
    !isSampleProject && !isLoadingProjection && lastKnownSequence !== undefined;

  useNotificationSocket(
    { accountId, projectId, branchId: branchId },
    {
      onEvent: (event: EventPB) => {
        const eventPayloadType = event.payload?.$case;
        const currentSequence =
          store.getState().streamTracker.lastKnownSequence;
        const optimisticCommands =
          store.getState().streamTracker.optimisticCommands;
        const lastOptimisticSequence =
          optimisticCommands.at(-1)?.optimisticSequenceNumber;
        const sequenceGap = event.sequence
          ? event.sequence.toNumber() - (currentSequence || 0)
          : 0;

        // toast for debugging sequence gaps (dev only)
        if (process.env.NEXT_PUBLIC_SHOW_LIVE_UPDATES === 'true') {
          toast.info({
            title: 'Live update',
            description: `Event: ${eventPayloadType} | Seq: ${event.sequence} | Local: ${currentSequence} | Gap: ${sequenceGap}`,
          });
        }
        void handleServerEvents(
          EventBatch.create({
            events: [event],
          }),
        );

        agentStreamLogger?.info('Event batch received', {
          ...event,
          localSequence: currentSequence,
          lastOptimisticSequence,
          sequenceGap,
          hasOptimisticCommands: optimisticCommands.length > 0,
        });
      },
      onNotification: (notification: NotificationPB) => {
        agentStreamLogger?.info('Notification received', {
          ...notification,
        });
        void handleServerNotification(notification);
      },
      onError: err => {
        agentStreamLogger?.error('Pingu error', {
          error: err,
        });

        // Show user-facing error notification
        toast.error(AGENT_STREAM_CONTEXT.toast.connection.error);
      },
      onConnect: () => {
        agentStreamLogger?.info('Pingu connected', {});

        if (disconnectedTimeout.current) {
          clearTimeout(disconnectedTimeout.current);
          disconnectedTimeout.current = null;
        }

        // Re-enable disconnect toast after successful connection
        shouldShowDisconnectToast.current = true;

        // Dismiss any existing error notifications
        toast.dismiss(AGENT_STREAM_CONTEXT.customIds.connectionError);
        toast.dismiss(AGENT_STREAM_CONTEXT.customIds.connectionLost);
      },
      onDisconnect: () => {
        agentStreamLogger?.info('Pingu disconnected', {});

        // Only show disconnection toast if feature flag is enabled and not intentionally disconnecting
        if (
          shouldShowDisconnectToast.current &&
          !isTemplateEngineUser &&
          isDisconnectToastEnabled
        ) {
          disconnectedTimeout.current = setTimeout(() => {
            toast.warning(AGENT_STREAM_CONTEXT.toast.connection.lost);
            disconnectedTimeout.current = null;
          }, 1000);
        }
      },
    },
    isSocketEnabled,
  );

  // Disable disconnect toast when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      shouldShowDisconnectToast.current = false;
    };
  }, []);

  // Suppress disconnect toast during intentional branch switches
  useEffect(() => {
    if (previousBranchId.current !== branchId) {
      shouldShowDisconnectToast.current = false;
      previousBranchId.current = branchId;
    }
  }, [branchId]);

  const dispatchEventBatch = useCallback(
    (...actions: PayloadAction[]) => {
      if (isPreviewMode) {
        // we do not allow optimistic commands in preview mode
        return Promise.reject(new Error('Cannot send events in preview mode'));
      }

      if (isSampleProject) {
        // Sample projects are read-only
        return Promise.reject(
          new Error('Cannot send events in sample project'),
        );
      }

      const optimisticCommands =
        store.getState().streamTracker.optimisticCommands;
      // last command represents the latest optimistic command we have sent over to BE
      // so as we want to send another command, if we have lastCommand, we
      // send sequence  + 1
      const lastCommand = optimisticCommands.at(-1);
      // currentHighestValidatedSequenceNumber = fully validated sequence number
      // any sequence > currentHighestValidatedSequenceNumber is optimistic
      const currentHighestValidatedSequenceNumber = lastCommand
        ? lastCommand.optimisticSequenceNumber
        : (store.getState().streamTracker.lastKnownSequence ?? 0);

      const currentTime = new Date().toISOString();
      const user = store.getState().auth.user?.email || 'Unknown user';

      const commandWithIds = actions.map(
        (
          action,
          idx,
        ): ActionWithCommandId & { optimisticSequenceNumber: number } => ({
          commandId: uuidv4(),
          action: {
            ...action,
            meta: {
              createdAt: currentTime,
              createdBy: user,
            },
          },
          optimisticSequenceNumber:
            // add 1 since we want optimistic sequence
            currentHighestValidatedSequenceNumber + idx + 1,
        }),
      );

      dispatch(addOptimisticCommands(commandWithIds));

      commandWithIds.forEach(({ action }) => {
        dispatch(action);
      });

      agentStreamLogger?.info('Dispatching Event Batch', {
        accountId,
        projectId,
        branchId,
        actions,
      });

      return new Promise(
        (
          resolve: CommandPromiseResolver['resolve'],
          reject: CommandPromiseResolver['reject'],
        ) => {
          queuedResolvers.current.push({ resolve, reject });
        },
      );
    },
    [
      isPreviewMode,
      isSampleProject,
      store,
      dispatch,
      branchId,
      accountId,
      projectId,
    ],
  );

  const { isBranchError } = useAutoCreateBranch({
    enabled: !isMultiDraft,
    accountId,
    projectId,
    branchId,
    actionQueue,
    lastKnownSequence: lastKnownSequence ?? 0,
  });

  // Send commands to server
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing
  useEffect(() => {
    if (
      actionQueue.length === 0 ||
      isSendingCommands ||
      isLoadingProjection ||
      isDeletingDraft ||
      isMergingDraft ||
      branchId === undefined ||
      branchId === MAIN_BRANCH_ID
    ) {
      return;
    }

    dispatch(clearActionQueue());

    const resolvers = [...queuedResolvers.current];
    queuedResolvers.current = [];

    const expectedSequenceNumber = actionQueue[0].optimisticSequenceNumber;

    agentStreamLogger?.info('Sending Commands to Server', {
      accountId,
      projectId,
      branchId,
      actionQueue,
      expectedSequenceNumber,
    });

    sendCommandsToServer(
      {
        // lastKnownSequence is the sequence number of the last fully validated command
        lastKnownSequence: Long.fromInt(expectedSequenceNumber - 1),
        actions: actionQueue,
      },
      {
        onSuccess(response) {
          if ('error' in response) {
            resolvers.forEach(({ reject }) => reject(response.error));

            dispatch(clearOptimisticCommands());
            dispatch(clearActionQueue());

            dispatch(rollBackProjection({ projection: acceptedState.current }));

            toast.error(AGENT_STREAM_CONTEXT.toast.validation.error);

            agentStreamLogger?.error('Validation error received', {
              accountId,
              projectId,
              branchId,
              error: response.error,
            });
          } else {
            const lastKnownSequence =
              store.getState().streamTracker.lastKnownSequence;
            const responseSequenceId = Long.fromValue(
              response.sequenceId,
            ).toInt();
            if (!lastKnownSequence || responseSequenceId > lastKnownSequence) {
              dispatch(
                setLastKnownSequence({ sequenceId: responseSequenceId }),
              );
            }
            agentStreamLogger?.info('Events accepted by the server', {
              accountId,
              projectId,
              branchId,
              events: response.events,
            });

            resolvers.forEach(({ resolve }) => resolve(response.events));

            if (projectId && isGlotCreatedProject(projectId)) {
              trackPendoEvent(PLG_PENDO_EVENTS.AGENT_MODIFIED_POST_GLOT, {
                accountId,
                projectId,
                commandCount: actionQueue.length,
              });
            }
          }
        },
        onError: error => {
          resolvers.forEach(({ reject }) => reject(error));
          dispatch(clearOptimisticCommands());
          dispatch(clearActionQueue());

          dispatch(rollBackProjection({ projection: acceptedState.current }));

          toast.error(AGENT_STREAM_CONTEXT.toast.network.error);

          agentStreamLogger?.error('Network error received', {
            accountId,
            projectId,
            branchId,
            error,
          });
        },
      },
    );
  }, [
    actionQueue,
    branchId,
    isSendingCommands,
    dispatch,
    sendCommandsToServer,
    store,
    toast,
    accountId,
    projectId,
    isLoadingProjection,
    isDeletingDraft,
    isMergingDraft,
    queuedResolvers,
  ]);

  // Loading until both: first API load completes AND projection is hydrated into Redux
  const isLoading = isFirstLoad || !isProjectionHydrated;

  const contextValue = useMemo(
    () => ({
      dispatchEventBatch,
      deleteBranch: deleteDraft,
      isDeletingBranch: isDeletingDraft,
      mergeBranch: mergeDraft,
      isMergingBranch: isMergingDraft,
      isLoading,
    }),
    [
      dispatchEventBatch,
      deleteDraft,
      isDeletingDraft,
      mergeDraft,
      isMergingDraft,
      isLoading,
    ],
  );

  // Check if this is a branch 404 error
  const isBranchNotFound =
    projectionError &&
    projectionError instanceof HTTPError &&
    projectionError.httpStatus === 404 &&
    branchId !== MAIN_BRANCH_ID;

  if (isBranchNotFound && !isLoading) {
    return <ErrorPage type={ErrorStatus.NOT_FOUND} />;
  }

  if ((projectionError || isBranchError) && !isLoading) {
    return <ErrorPage type={ErrorStatus.UNHANDLED_ERROR} />;
  }

  return (
    <AgentStreamContext.Provider value={contextValue}>
      {isLoading && !isAgentWizard && !hideLoader ? (
        <Loader fullScreen inProjectPage logo />
      ) : (
        children
      )}
      {isMultiDraft && (
        <CreateBranchModal
          accountId={accountId}
          open={!!actionQueue.length && branchId === MAIN_BRANCH_ID}
          projectId={projectId}
          onBranchCreated={branchId => dispatch(setBranchId({ branchId }))}
          onCancel={() => {
            dispatch(clearOptimisticCommands());
            dispatch(rollBackProjection({ projection: acceptedState.current }));
          }}
        />
      )}
    </AgentStreamContext.Provider>
  );
};

export const AgentStreamProvider = ({
  accountId,
  projectId,
  branchId,
  children,
  hideLoader,
}: AgentStreamProviderProps) => {
  const { isLoadingFeatures } = useIsFeatureOn();

  const { project } = useProject();
  const { inSampleProject } = useSampleProject();

  if (inSampleProject) {
    return (
      <AgentStreamProviderInternal
        accountId={accountId}
        branchId={branchId}
        hideLoader={hideLoader}
        projectId={projectId}
      >
        {children}
      </AgentStreamProviderInternal>
    );
  }

  // Only bypass for intent projects
  if (project?.type === AssistantType.INTENT) {
    return (
      <AgentStreamContext.Provider
        value={{
          deleteBranch: async () => {},
          dispatchEventBatch: async () => [],
          isDeletingBranch: false,
          isMergingBranch: false,
          mergeBranch: (async (
            _?: string,
            __?: ConflictResolution[],
          ) => {}) as any,
          // prevent flash
          isLoading: isLoadingFeatures,
        }}
      >
        {children}
      </AgentStreamContext.Provider>
    );
  }

  return (
    <AgentStreamProviderInternal
      accountId={accountId}
      branchId={branchId}
      hideLoader={hideLoader}
      projectId={projectId}
    >
      <PreviewVersionProvider>{children}</PreviewVersionProvider>
    </AgentStreamProviderInternal>
  );
};

export const useServerDispatch = () =>
  useContext(AgentStreamContext).dispatchEventBatch;
export const useDeleteDraftStream = () =>
  pick(useContext(AgentStreamContext), 'deleteBranch', 'isDeletingBranch');
export const useMergeDraftStream = () =>
  pick(useContext(AgentStreamContext), 'mergeBranch', 'isMergingBranch');
export const useAgentStreamLoading = () =>
  useContext(AgentStreamContext).isLoading;
